import { Alert, Box, Grid2, Stack, Typography } from '@mui/material'
import { Dispatch, useMemo, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import { Application_Type, Criteria_Group_Config } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  mapCriteriaGroupsListToOptionsList,
  mapFetchedApplicationTypesToCheckboxData,
} from 'src/screens/administration/configuration-management/criteria-configuration/evaluation-criteria/edit/EvaluationCriteriaFormUtils'
import { CRITERIA_TYPE_TYPE } from 'src/shared/constants/assessment-constants'
import {
  CONFIGURATION_STATUS_TYPE,
  LEVEL_TYPE,
  PROCESS,
  PROCESS_TYPE,
  TEXT_LENGTH,
} from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { CheckboxData, CheckboxGroupField } from 'src/shared/form/control/CheckboxGroupField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { RadioButtonGroupField, RadioData } from 'src/shared/form/control/RadioButtonGroupField'
import { TextField } from 'src/shared/form/control/TextField'
import { composeValidators, maxChar, required } from 'src/shared/form/validation/validators'
import { useUserLocale } from 'src/user/UserContext'
import {
  getConfigStatusOptions,
  getLevelOptions,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { usePrompt } from 'src/routing/routing-hooks'
import {
  getCriteriaConfigProcessOptions,
  getCriteriaTypeOptions,
} from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { createDecorators } from 'src/shared/form/utils/decorators'

export interface EvaluationCriteriaFormValues {
  criteriaType: string
  criteriaId: string
  status: CONFIGURATION_STATUS_TYPE
  deDescription: string
  enDescription: string
  frDescription: string
  itDescription: string
  processAlloc: PROCESS_TYPE
  pfKapCriteriaGroup: number | null
  pfKapApplicationTypes: number[]
  pfPgvCriteriaGroup: number | null
  pfPgvApplicationTypes: number[]
  factsheetCriteriaGroup: number | null
  factsheetLevels: LEVEL_TYPE[]
}

interface CriteriaConfigurationEvaluationCriteriaFormProps {
  mode: 'EDIT' | 'CREATE'
  onSubmit: (values: EvaluationCriteriaFormValues, setFormDirty: Dispatch<boolean>) => Promise<void>
  initialValues: EvaluationCriteriaFormValues
  criteriaGroups: Criteria_Group_Config[]
  applicationTypes: Application_Type[]
}

export const CriteriaConfigurationEvaluationCriteriaForm = ({
  mode,
  onSubmit,
  initialValues,
  criteriaGroups,
  applicationTypes,
}: CriteriaConfigurationEvaluationCriteriaFormProps) => {
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()
  const decorators = createDecorators()

  const [formDirty, setFormDirty] = useState<boolean>(false)

  const [selectedCriteriaType, setSelectedCriteriaType] = useState<CRITERIA_TYPE_TYPE>(
    initialValues.criteriaType as CRITERIA_TYPE_TYPE,
  )

  const criteriaTypeFieldOptions: Option[] = useMemo(() => getCriteriaTypeOptions(getMessage), [getMessage])
  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])
  const processAllocOptions: RadioData[] = useMemo(() => getCriteriaConfigProcessOptions(getMessage), [getMessage])
  const factsheetLevelsOptions: CheckboxData[] = useMemo(() => getLevelOptions(getMessage), [getMessage])

  const pfKapGroupsOptions = useMemo(
    () =>
      mapCriteriaGroupsListToOptionsList(
        criteriaGroups.filter(
          (cg) => cg.group_processes.includes(PROCESS.PF_KAP) && cg.criteria_types.includes(selectedCriteriaType),
        ),
        getMessage,
        locale,
      ),
    [criteriaGroups, getMessage, locale, selectedCriteriaType],
  )

  const pfPgvGroupsOptions = useMemo(
    () =>
      mapCriteriaGroupsListToOptionsList(
        criteriaGroups.filter(
          (cg) => cg.group_processes.includes(PROCESS.PF_PGV) && cg.criteria_types.includes(selectedCriteriaType),
        ),
        getMessage,
        locale,
      ),
    [criteriaGroups, getMessage, locale, selectedCriteriaType],
  )

  const factsheetGroupsOptions = useMemo(
    () =>
      mapCriteriaGroupsListToOptionsList(
        criteriaGroups.filter(
          (cg) => cg.group_processes.includes(PROCESS.FACTSHEET) && cg.criteria_types.includes(selectedCriteriaType),
        ),
        getMessage,
        locale,
      ),
    [criteriaGroups, getMessage, locale, selectedCriteriaType],
  )

  const applicationTypeOptionsPerProcess = useMemo(
    () => mapFetchedApplicationTypesToCheckboxData(applicationTypes, getMessage),
    [applicationTypes, getMessage],
  )

  usePrompt(getMessage('notification.form.unsaved.changes'), formDirty)

  return (
    <Stack spacing={2}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<EvaluationCriteriaFormValues>
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values, setFormDirty)}
        decorators={decorators}
        mutators={{
          clearCriteriaGroupFieldsOnCriteriaTypeChange: (_, state, utils) => {
            if (state.lastFormState?.modified?.['criteriaType']) {
              utils.changeValue(state, 'pfKapCriteriaGroup', () => null)
              utils.changeValue(state, 'pfPgvCriteriaGroup', () => null)
              utils.changeValue(state, 'factsheetCriteriaGroup', () => null)
            }
          },
          clearCriteriaGroupFieldsOnProcessChange: ([currProcess], state, utils) => {
            if (currProcess !== PROCESS.PF_KAP) {
              utils.changeValue(state, 'pfKapCriteriaGroup', () => null)
              setTimeout(() => {
                utils.resetFieldState('pfKapCriteriaGroup')
              }, 0)
            }
            if (currProcess !== PROCESS.PF_PGV) {
              utils.changeValue(state, 'pfPgvCriteriaGroup', () => null)
              setTimeout(() => {
                utils.resetFieldState('pfPgvCriteriaGroup')
              }, 0)
            }
            if (currProcess !== PROCESS.FACTSHEET) {
              utils.changeValue(state, 'factsheetCriteriaGroup', () => null)
              setTimeout(() => {
                utils.resetFieldState('factsheetCriteriaGroup')
              }, 0)
            }
          },
          clearApplicationTypeFieldsOnProcessChange: ([currProcess], state, utils) => {
            if (currProcess !== PROCESS.PF_KAP) {
              utils.changeValue(state, 'pfKapApplicationTypes', () => [])
              setTimeout(() => {
                utils.resetFieldState('pfKapApplicationTypes')
              }, 0)
            }
            if (currProcess !== PROCESS.PF_PGV) {
              utils.changeValue(state, 'pfPgvApplicationTypes', () => [])
              setTimeout(() => {
                utils.resetFieldState('pfPgvApplicationTypes')
              }, 0)
            }
            if (currProcess !== PROCESS.FACTSHEET) {
              utils.changeValue(state, 'factsheetLevels', () => [])
              setTimeout(() => {
                utils.resetFieldState('factsheetLevels')
              }, 0)
            }
          },
        }}
        render={({ handleSubmit, form, values }) => {
          return (
            <form id="evaluation-criteria-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={criteriaTypeFieldOptions}
                      name="criteriaType"
                      label={getMessage('label.criteria_type')}
                      disableClearable={true}
                      required
                      validate={required()}
                      disabled={mode === 'EDIT'}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      name="criteriaId"
                      label={getMessage('label.configuration.evaluation.criteria.create.criteria_id')}
                      validate={composeValidators(required(), maxChar(TEXT_LENGTH.EVALUATION_CRITERIA_ID))}
                      required
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable={true}
                      required
                      validate={required()}
                    />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.description')}</Typography>

                <HtmlEditorField
                  name="deDescription"
                  label={getMessage('label.language.de')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
                <HtmlEditorField
                  name="enDescription"
                  label={getMessage('label.language.en')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
                <HtmlEditorField
                  name="frDescription"
                  label={getMessage('label.language.fr')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
                <HtmlEditorField
                  name="itDescription"
                  label={getMessage('label.language.it')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />

                <Typography variant="h2">{getMessage('label.configuration.grid.allocation')}</Typography>

                <Grid2 container columnSpacing={5}>
                  <RadioButtonGroupField
                    name="processAlloc"
                    required
                    color="primary"
                    label={
                      <Typography variant={'subtitle2'} component="span" sx={{ ml: -2 }}>
                        {getMessage('label.process')}
                      </Typography>
                    }
                    data={processAllocOptions}
                    withDividersBetween
                    formGroupProps={{
                      sx: {
                        flexWrap: 'initial',
                      },
                    }}
                    disabled={mode === 'EDIT'}
                    render={(item, _) => {
                      const disabledCriteriaGroupAndAppType = values.processAlloc !== item.value || mode === 'EDIT'
                      const validateCriteriaGroupAndAppType =
                        values.processAlloc === item.value && !disabledCriteriaGroupAndAppType ? required() : undefined
                      switch (item.value) {
                        case PROCESS.PF_KAP:
                          return (
                            <Stack width="80%" paddingTop={3} spacing={2}>
                              <AutoCompleteField
                                key={`${validateCriteriaGroupAndAppType}`}
                                options={pfKapGroupsOptions}
                                name="pfKapCriteriaGroup"
                                label={getMessage('label.criteria_group')}
                                getOptionKey={(option) => option.value?.toString() ?? ''}
                                required
                                validate={validateCriteriaGroupAndAppType}
                                disabled={disabledCriteriaGroupAndAppType}
                                renderOptionAsHtml
                                renderOptionLabelPrefix={(opt) => (
                                  <Box display="flex" flexDirection="row" gap={1} marginRight={1}>
                                    <span>{opt.sortNumber}</span>
                                    <span>-</span>
                                  </Box>
                                )}
                              />
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={disabledCriteriaGroupAndAppType ? { color: 'grey' } : {}}
                                >
                                  {getMessage('label.application_types')}
                                </Typography>
                                <CheckboxGroupField
                                  key={`${validateCriteriaGroupAndAppType}`}
                                  name="pfKapApplicationTypes"
                                  color="primary"
                                  formGroupProps={{ row: true }}
                                  data={applicationTypeOptionsPerProcess.pfKapApplicationTypesCheckbox}
                                  validate={validateCriteriaGroupAndAppType}
                                  disabled={disabledCriteriaGroupAndAppType}
                                />
                              </Box>
                            </Stack>
                          )
                        case PROCESS.PF_PGV:
                          return (
                            <Stack width="80%" paddingTop={3} spacing={2}>
                              <AutoCompleteField
                                key={`${validateCriteriaGroupAndAppType}`}
                                options={pfPgvGroupsOptions}
                                name="pfPgvCriteriaGroup"
                                label={getMessage('label.criteria_group')}
                                getOptionKey={(option) => option.value?.toString() ?? ''}
                                required
                                validate={validateCriteriaGroupAndAppType}
                                disabled={disabledCriteriaGroupAndAppType}
                                renderOptionAsHtml
                                renderOptionLabelPrefix={(opt) => (
                                  <Box display="flex" flexDirection="row" gap={1} marginRight={1}>
                                    <span>{opt.sortNumber}</span>
                                    <span>-</span>
                                  </Box>
                                )}
                              />
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={disabledCriteriaGroupAndAppType ? { color: 'grey' } : {}}
                                >
                                  {getMessage('label.application_types')}
                                </Typography>
                                <CheckboxGroupField
                                  key={`${validateCriteriaGroupAndAppType}`}
                                  name="pfPgvApplicationTypes"
                                  color="primary"
                                  formGroupProps={{ row: true }}
                                  data={applicationTypeOptionsPerProcess.pfPgvApplicationTypesCheckbox}
                                  validate={validateCriteriaGroupAndAppType}
                                  disabled={disabledCriteriaGroupAndAppType}
                                />
                              </Box>
                            </Stack>
                          )
                        case PROCESS.FACTSHEET:
                          return (
                            <Stack width="80%" paddingTop={3} spacing={2}>
                              <AutoCompleteField
                                key={`${validateCriteriaGroupAndAppType}`}
                                options={factsheetGroupsOptions}
                                name="factsheetCriteriaGroup"
                                label={getMessage('label.criteria_group')}
                                getOptionKey={(option) => option.value?.toString() ?? ''}
                                required
                                validate={validateCriteriaGroupAndAppType}
                                disabled={disabledCriteriaGroupAndAppType}
                                renderOptionAsHtml
                                renderOptionLabelPrefix={(opt) => (
                                  <Box display="flex" flexDirection="row" gap={1} marginRight={1}>
                                    <span>{opt.sortNumber}</span>
                                    <span>-</span>
                                  </Box>
                                )}
                              />
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={disabledCriteriaGroupAndAppType ? { color: 'grey' } : {}}
                                >
                                  {getMessage('label.levels')}
                                </Typography>
                                <CheckboxGroupField
                                  key={`${validateCriteriaGroupAndAppType}`}
                                  name="factsheetLevels"
                                  color="primary"
                                  formGroupProps={{ row: true }}
                                  data={factsheetLevelsOptions}
                                  validate={validateCriteriaGroupAndAppType}
                                  disabled={disabledCriteriaGroupAndAppType}
                                />
                              </Box>
                            </Stack>
                          )
                        default:
                          return <></>
                      }
                    }}
                  />
                  <OnChange name="processAlloc">
                    {(currProcess, _) => {
                      if (mode !== 'EDIT') {
                        form.mutators.clearCriteriaGroupFieldsOnProcessChange(currProcess)
                        form.mutators.clearApplicationTypeFieldsOnProcessChange(currProcess)
                      }
                    }}
                  </OnChange>
                  <OnChange name="criteriaType">
                    {(currCriteriaType) => {
                      if (mode !== 'EDIT') {
                        setSelectedCriteriaType(currCriteriaType)
                        form.mutators.clearCriteriaGroupFieldsOnCriteriaTypeChange()
                      }
                    }}
                  </OnChange>
                </Grid2>
              </Stack>

              <FormSpy
                subscription={{ dirty: true }}
                onChange={(props: { dirty: boolean }) => {
                  setFormDirty(props.dirty)
                }}
              />
            </form>
          )
        }}
      />
    </Stack>
  )
}
